import dayjs from "dayjs";
import { z } from "zod";
import { SectionFourDocTypeTypes } from "./documents-four-enum";

export const JournalSectionFormSchema = z.object({
  organization: z.object({
    company: z.object({
      companyId: z.string().uuid(),
    }),
    projectParticipantId: z.string().uuid(),
  }),
  userCompany: z
    .object({
      userCompanies: z
        .array(
          z.object({
            userCompaniesHistoryId: z.string().uuid(),
            shortName: z.string().optional(),
            position: z.string().optional(),
          }),
        )
        .optional(),
      userId: z.string().uuid(),
      userSurname: z.string().optional(),
      userFirstName: z.string().optional(),
      userMiddleName: z.string().optional(),
      userEmail: z.string().optional(),
      userMobilePhone: z.string().optional(),
    })
    .nullable() // <== вот это ключ
    .refine((val) => val === null || typeof val === "object", {
      message: "Некорректный пользователь",
    }),

  workType: z.string().min(1, "Укажите вид работ"),
  startDate: z.date(),
  endDate: z.date().nullable().optional(),
  signer: z.string().uuid(),
  actionDetails: z.string().optional(),
});

export type JournalSectionFormType = z.infer<typeof JournalSectionFormSchema>;

const preprocessDate = z.preprocess(
  (arg) => {
    if (dayjs.isDayjs(arg)) return arg.toDate();
    if (typeof arg === "string") return new Date(arg);
    return arg;
  },
  z.date({
    required_error: "Укажите сроки проведения контрольного мероприятия",
    invalid_type_error: "Неверная дата",
  }),
);

export const JournalSectionFormEditSchema = z
  .object({
    // сведения о СК
    constructionControlInfo: z
      .string()
      .nonempty("Укажите сведения об отсутствии недостатков"),

    // даты
    controlStartDate: preprocessDate,
    controlEndDate: preprocessDate,

    // недостатки
    defectsExist: z.enum(["true", "false"]),
    defectsAbsenceInfo: z
      .string()
      .max(255, "Максимальная длина — 255 символов")
      .optional()
      .or(z.literal("")),
    defectDescriptionText: z
      .string({ required_error: "Описание недостатков обязательно" })
      .optional(),
    defectFixingPlanDate: preprocessDate.optional(),

    // местоположение
    structuralElementName: z.string().optional(),
    locationDescription: z.string().optional(),
    locationType: z
      .enum([
        "AXES",
        "RANKS",
        "MARKS",
        "LOCATION",
        "TIERS",
        "SECTIONS",
        "PREMISES",
        "FLOORS",
      ])
      .optional(),

    // подписант
    controlRepresentative: z.string({
      required_error: "Выберите представителя",
    }),

    // причина изменения (для обновления подписанной записи)
    actionDetails: z.string().optional(),

    // документы
    docs: z
      .array(
        z.object({
          docId: z.string(),
          docType: z.nativeEnum(SectionFourDocTypeTypes),
          docInfo: z.string().nullable().optional(),
        }),
      )
      .optional(),

    works: z
      .array(
        z.object({
          itemNoVersionId: z.string(),
          section3ListId: z.string(),
          selected: z.boolean(),
        }),
      )
      .optional(),

    // файлы
    itemPartOne: z
      .object({
        otherDocuments: z
          .array(
            z.object({
              fileMetadata: z.object({
                fileNumber: z.number(),
                fileName: z.string(),
                fileType: z.string(),
                fileContentLength: z.number(),
                id: z.string().optional(),
                fileId: z.string().optional(),
              }),
            }),
          )
          .optional(),
        files: z.array(z.any()).optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.controlStartDate &&
      data.controlEndDate &&
      !dayjs(data.controlEndDate).isAfter(dayjs(data.controlStartDate))
    ) {
      ctx.addIssue({
        path: ["controlEndDate"],
        code: z.ZodIssueCode.custom,
        message: "Дата окончания должна быть позже даты начала",
      });
    }

    if (data.defectsExist === "true" && data.defectsExist !== undefined) {
      if (!data.defectDescriptionText?.trim()) {
        ctx.addIssue({
          path: ["defectDescriptionText"],
          code: z.ZodIssueCode.custom,
          message: "Описание недостатков обязательно",
        });
      }

      if (!data.docs || data.docs.length === 0) {
        ctx.addIssue({
          path: ["docs"],
          code: z.ZodIssueCode.custom,
          message: "Добавьте хотя бы один документ",
        });
      }

      const hasRegulationDoc = data.docs?.some(
        (doc) => doc.docType === SectionFourDocTypeTypes.REGULATION_DOC,
      );
      if (!hasRegulationDoc) {
        ctx.addIssue({
          path: ["docs"],
          code: z.ZodIssueCode.custom,
          message:
            "Необходимо добавить хотя бы один документ типа REGULATION_DOC",
        });
      }

      if (!data.defectFixingPlanDate) {
        ctx.addIssue({
          path: ["defectFixingPlanDate"],
          code: z.ZodIssueCode.custom,
          message: "Укажите срок устранения недостатков",
        });
      }
    }
  });
